

// src/components/MapShell.js
import React, { useEffect, useState } from 'react';

import RestoreSidebarButton from './helpers/RestoreSideBarButton.js';
import ShowLeftSidebarIcon from '../images/ShowLeftSideBarButtonLight@3x.png';
import ShowLeftSidebarIconHover from '../images/ShowLeftSideBarButtonBlue@3x.png';
import ShowRightSidebarIcon from '../images/ShowRightSideBarButtonLight@3x.png';
import ShowRightSidebarIconHover from '../images/ShowRightSideBarButtonBlue@3x.png';


/**
 * MapShell
 * -----------------------------------------------------------------------------
 * Generic “map page” frame: Left sidebar • Map • Right sidebar.
 * - Keeps collapse/restore UX identical to your current page.
 * - Makes no assumptions about what’s inside each slot.
 *
 * Props:
 *  - leftContent:      ReactNode (e.g., Layers/Plans OR Stock filters)
 *  - mapContent:       ReactNode (e.g., your MapComponent OR stock map)
 *  - rightContent:     ReactNode (e.g., Plan/Land details OR Listing details)
 *  - presentationModeSize: number (1..5) to mirror your current auto-collapse rules
 *  - defaultLeftOpen?: boolean (default true)
 *  - defaultRightOpen?: boolean (default true)
 *  - backgroundColor?: string (default '#EEF2F5')
 */


export default function MapShell({
  leftContent,
  mapContent,
  rightContent,
  presentationModeSize,
  defaultLeftOpen = true,
  defaultRightOpen = true,
  desktopRightSidebarDefaultOpen = true,
  rightSidebarAvailable = true,
  autoCollapseByPresentationMode = true,
  backgroundColor = '#EEF2F5',
  onRequestRightSidebarOpen,
  onRequestRightSidebarClose,
}) {

  const [isLeftSidebarExpanded, setIsLeftSidebarExpanded] = useState(defaultLeftOpen);
  const [isRightSidebarExpanded, setIsRightSidebarExpanded] = useState(defaultRightOpen);

  const rightSidebarWidth = (() => {
    if (presentationModeSize === 1) return '350px';  // Big!
    if (presentationModeSize === 2) return '300px';  // Bigish...
    if (presentationModeSize === 3) return '250px';                                // Not as big.
    if (presentationModeSize === 4 || presentationModeSize === 5) return '250px';  // Little!
    return '300px';  // Default.
  })();


  // Scale this mofo depending on screen size.
  useEffect(() => {
    if (!autoCollapseByPresentationMode) {
      return;
    }
    switch (presentationModeSize) {
      case 1:
      case 2:
        setIsLeftSidebarExpanded(true);
        setIsRightSidebarExpanded(desktopRightSidebarDefaultOpen);
        break;
      case 3:
      case 4:
        setIsLeftSidebarExpanded(true);
        setIsRightSidebarExpanded(false);
        break;
      case 5:
        setIsLeftSidebarExpanded(false);
        setIsRightSidebarExpanded(false);
        break;
      default:
        setIsLeftSidebarExpanded(true);
        setIsRightSidebarExpanded(true);
        break;
    }
  }, [presentationModeSize, autoCollapseByPresentationMode, desktopRightSidebarDefaultOpen]);


  // Use this when a marker is tapped on the stock page in case the right sidebar is collapsed.
  useEffect(() => {
    if (onRequestRightSidebarOpen) {
      //console.log('[MapShell] registering openRightSidebar handler');
      const openFn = () => {
        //console.log('[MapShell] openRightSidebar() invoked → expanding right sidebar');
        setIsRightSidebarExpanded(true);
      };
      onRequestRightSidebarOpen(openFn);
    }
  }, [onRequestRightSidebarOpen]);

  useEffect(() => {
    if (onRequestRightSidebarClose) {
      const closeFn = () => {
        setIsRightSidebarExpanded(false);
      };
      onRequestRightSidebarClose(closeFn);
    }
  }, [onRequestRightSidebarClose]);


  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
        backgroundColor,
      }}
    >
      {/* LEFT SIDEBAR */}
      <div
        style={{
          flexBasis: isLeftSidebarExpanded ? '220px' : 0,
          flexShrink: 0,
          transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
          opacity: isLeftSidebarExpanded ? 1 : 0,
          height: '100%',
          display: 'flex',
          borderRight: isLeftSidebarExpanded ? '0px solid #596A99' : 'none',
          overflow: 'hidden',
        }}
      >
        {isLeftSidebarExpanded &&
          React.isValidElement(leftContent) &&
          React.cloneElement(leftContent, {
            // Your Left sidebar expects these props
            isVisible: true,
            onRequestClose: () => setIsLeftSidebarExpanded(false),
          })}
      </div>

      {/* MAP AREA */}
      <div
        style={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: 0,
          position: 'relative',
          transition: 'all 0.3s ease',
        }}
      >
        {mapContent}

        {/* Restore buttons (match your existing UX) */}
        {!isLeftSidebarExpanded && (
          <RestoreSidebarButton
            onClick={() => setIsLeftSidebarExpanded(true)}
            position="left"
            defaultIcon={ShowLeftSidebarIcon}
            hoverIcon={ShowLeftSidebarIconHover}
            title="Show Left Sidebar"
          />
        )}

        {!isRightSidebarExpanded && rightSidebarAvailable && (
          <RestoreSidebarButton
            onClick={() => setIsRightSidebarExpanded(true)}
            position="right"
            defaultIcon={ShowRightSidebarIcon}
            hoverIcon={ShowRightSidebarIconHover}
            title="Show Right Sidebar"
          />
        )}
      </div>

      {/* RIGHT SIDEBAR */}
      <div
        style={{
          flexBasis: isRightSidebarExpanded ? rightSidebarWidth : 0,
          flexShrink: 0,
          transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
          opacity: isRightSidebarExpanded ? 1 : 0,
          height: '100%',
          display: 'flex',
          borderLeft: isRightSidebarExpanded ? '0px solid #596A99' : 'none',
          overflow: 'hidden',
        }}
      >
        {isRightSidebarExpanded &&
          React.isValidElement(rightContent) &&
          React.cloneElement(rightContent, {
            // Your Right sidebar expects these props
            isExpanded: true,
            onRequestClose: () => setIsRightSidebarExpanded(false),
          })}
      </div>
    </div>
  );
}
